import { useQuery } from "@tanstack/react-query";
import { Loader2, TrendingUp, Users, Activity, Clock } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";

export const PromptAnalytics = () => {
  const { invokeAdmin } = useAdminEdgeFunction();

  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ["prompt-analytics"],
    queryFn: async () => {
      const { data, error } = await invokeAdmin("get-prompt-analytics", {
        body: {},
        showErrorToast: true,
      });

      if (error) throw error;
      return data?.analytics || data;
    },
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Prompt Analytics</h2>
          <p className="text-muted-foreground">Loading performance metrics...</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                <div className="h-4 w-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded mb-2" />
                <div className="h-3 w-20 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Prompt Analytics</h2>
          <p className="text-muted-foreground">Performance metrics and usage statistics</p>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Activity className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">Failed to load analytics</p>
            <p className="text-muted-foreground">Please try again later</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Prompt Analytics</h2>
        <p className="text-muted-foreground">
          Performance metrics and usage statistics for prompt templates
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalTemplates || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.activeTemplates || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Versions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalVersions || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.activeVersions || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Rollouts</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.activeRollouts || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently running
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalUsage || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Prompt invocations
            </p>
          </CardContent>
        </Card>
      </div>

      {analytics?.templateStats && analytics.templateStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Template Usage Statistics</CardTitle>
            <CardDescription>
              Usage statistics for each prompt template
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.templateStats.map((stat: any) => (
                <div
                  key={stat.template_id}
                  className="flex items-center justify-between border-b pb-4 last:border-0"
                >
                  <div>
                    <p className="font-medium">{stat.template_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {stat.function_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{stat.usage_count || 0}</p>
                    <p className="text-xs text-muted-foreground">uses</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(!analytics?.templateStats || analytics.templateStats.length === 0) && (
        <Card>
          <CardContent className="text-center py-12">
            <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">No usage data yet</p>
            <p className="text-muted-foreground">
              Usage statistics will appear here once prompts are used
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
